import { deepDecode, deepEncode } from "../utils";
import Api from "../singleton/api";
import IParamItem from "../interface/param-item";
import IParamBased from "../interface/param-based";
import IApiParamFactory from "../interface/api/api-param-factory";
import ParamStorage from "../enum/param-storage";
import ParamOption from "../enum/param-option";
import ErrorCode from "../enum/error-code";

export default class ParameterStorage<TItem extends IParamBased> {

    public id: ParamStorage;

    private factory: IApiParamFactory | null = null;

    private option: ParamOption;

    private ondemand: string[];

    constructor(id: ParamStorage, option: ParamOption, ondemand: string[]) {
        this.id = id;
        this.ondemand = ondemand;
        this.option = option;
    }

    /* Public Methods */

    public async init(): Promise<void> {
        this.factory = Api.getParameterFactory(this.id);
        if (!this.factory) {
            await Api.createParameterFactory(this.id, this.option);
            this.factory = Api.getParameterFactory(this.id);
        }
    }

    public getAll(): TItem[] {
        if (this.factory) {
            const paramItems: IParamItem[] = this.factory.getAll();
            return paramItems.map((paramItem: IParamItem) => this.toItem(paramItem));
        } else {
            throw ErrorCode.StorageNotInitialized;
        }
    }

    public get(id: number): TItem | null {
        if (this.factory) {
            const paramItem: IParamItem = this.factory.get(id);
            if (paramItem) {
                return this.toItem(paramItem) as TItem;
            }
            return null;
        } else {
            throw ErrorCode.StorageNotInitialized;
        }
    }

    public async load(id: number): Promise<TItem | null> {
        if (this.factory) {
            await this.loadStorage();
            const paramItem: IParamItem = this.factory.get(id);
            if (paramItem) {
                if (paramItem.ondemand === undefined) {
                    await Api.paramLoad(this.factory, id);
                }
                return this.get(id);
            }
            return null;
        } else {
            throw ErrorCode.StorageNotInitialized;
        }
    }

    public async loadAll(): Promise<TItem[]> {
        if (this.factory) {
            await this.loadStorage();
            const items: TItem[] = this.getAll();
            const result: TItem[] = [];
            for (let i = 0; i < items.length; i++) {
                result.push(await this.load(items[i].id as number) as TItem);
            }
            return result;
        } else {
            throw ErrorCode.StorageNotInitialized;
        }
    }

    public async create(item: TItem): Promise<TItem> {
        if (this.factory) {
            await this.loadStorage();
            const paramItem: IParamItem = this.toParam(item);
            const result: IParamItem = await Api.paramInsert(this.factory, paramItem);
            return this.get(result.id as number) as TItem;
        } else {
            throw ErrorCode.StorageNotInitialized;
        }
    }

    public async update(item: TItem): Promise<TItem> {
        if (this.factory) {
            if (item.id) {
                await this.loadStorage();
                const original: IParamItem = this.factory.get(item.id);
                if (original) {
                    const paramItem: IParamItem = this.toParam(item);
                    original.param = paramItem.param;
                    if (paramItem.ondemand) {
                        original.ondemand = paramItem.ondemand;
                    }
                    await Api.paramUpdate(this.factory, item.id as number);
                    return this.get(item.id) as TItem;
                } else {
                    throw ErrorCode.BadArguments;
                }
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.StorageNotInitialized;
        }
    }

    public async delete(id: number): Promise<number> {
        if (this.factory) {
            await this.loadStorage();
            return await Api.paramDelete(this.factory, id as number);
        } else {
            throw ErrorCode.StorageNotInitialized;
        }
    }

    public async bulk(toCreate: TItem[], toUpdate: TItem[], toDelete: number[]): Promise<TItem[]> {
        for (let i = 0; i < toDelete.length; i++) {
            await this.delete(toDelete[i]);
        }
        for (let i = 0; i < toUpdate.length; i++) {
            await this.update(toUpdate[i]);
        }
        const result: TItem[] = [];
        for (let i = 0; i < toCreate.length; i++) {
            result.push(await this.create(toCreate[i]));
        }
        return result;
    }

    public async bulkAll(items: TItem[]): Promise<TItem[]> {
        const toCreate: TItem[] = [];
        const toUpdate: TItem[] = [];
        const toDelete: number[] = [];
        const store: TItem[] = this.getAll();
        store.forEach((storeItem: TItem) => {
            const item: TItem | undefined = items.find((s: TItem) => s.id === storeItem.id);
            if (item) {
                toUpdate.push(item);
            } else {
                toDelete.push(storeItem.id as number);
            }
        });
        items.forEach((item: TItem) => {
            if (!item.id) {
                toCreate.push(item);
            }
        });
        return this.bulk(toCreate, toUpdate, toDelete);
    }

    public async loadStorage(): Promise<void> {
        if (this.factory && !this.factory.isLoaded()) {
            await Api.paramLoadValues(this.factory);
        }
    }

    /* Private Methods */

    private toParam(origin: TItem): IParamItem {
        const item: TItem = deepEncode(origin);
        const result: IParamItem = { param: {} };
        const keys: string[] = Object.keys(item);
        keys.forEach((key: string) => {
            if (key !== 'id') {
                if (this.ondemand.includes(key)) {
                    if (!result.ondemand) {
                        result.ondemand = {};
                    }
                    result.ondemand[key] = item[key as keyof TItem];
                } else {
                    result.param[key] = item[key as keyof TItem];
                }
            }
        });
        return result;
    }

    private toItem(original: IParamItem): TItem {
        const item: IParamItem = deepDecode(original);
        const result: Object = { id: item.id };
        Object.assign(result, item.param);
        if (item.ondemand) {
            Object.assign(result, item.ondemand);
        }
        return result as TItem;
    }

}